import { useState, useEffect } from 'react'
import {
  Box,
  Button,
  IconButton,
  Typography,
  useTheme,
  Avatar,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  Modal,
  Fade,
  Autocomplete,
  TextField,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import small_logo from 'components/assets/small_logo.svg'
import MenuOpenIcon from '@mui/icons-material/MenuOpen'
import MenuIcon from '@mui/icons-material/Menu'
import SettingsIcon from '@mui/icons-material/Settings'
import SchoolRounded from '@mui/icons-material/SchoolRounded'
import SwitchCamera from '@mui/icons-material/SwitchCamera'
import HelpIcon from '@mui/icons-material/Help'
import LogoutIcon from '@mui/icons-material/Logout'
import Language from '@mui/icons-material/Language'
import { ViewModule, ViewList } from '@mui/icons-material'
import Brightness4Icon from '@mui/icons-material/Brightness4'
import Brightness7Icon from '@mui/icons-material/Brightness7'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import PersonIcon from '@mui/icons-material/Person'
import { Auth } from 'aws-amplify'
import { toggleOrgMode } from 'state/ui'
import { showFormModal, setShowSideMenuText } from 'state/ui'
import FORM_MODALS from 'utils/ui/modals'
import ChatComponentGemini from 'components/layouts/ChatComponentGemini'
import { useTranslation } from 'react-i18next'
import { clearLocalOrgInfo } from 'state/auth'
import {
  transformYardList,
  USER_SELECTED_YARD_LOCAL_STORAGE_KEY,
  USER_SELECTED_YARD_ID_LOCAL_STORAGE_KEY,
} from 'state/auth'
import { ORG_MODE_LIST_OBJECT } from 'utils/ui/actions'

interface NavHeaderProps {
  onClose?: () => void
  toggleNavStyle: () => void
  useCategoryNav: boolean
  toggleTheme: () => void
  currentTheme: string
  showSettings?: boolean
}

interface YardOption {
  id: string
  name: string
}

const NavHeader: React.FC<NavHeaderProps> = ({
  onClose,
  toggleNavStyle,
  useCategoryNav,
  toggleTheme,
  currentTheme,
  showSettings = true,
}) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation('inboundTicket')

  const { organization, yardDetailsList, selectedYard, user } = useSelector((state: any) => state.auth)
  const { inOrgMode, showSideMenuText } = useSelector((state: any) => state.ui)
  const { appRouteLoadBoard, orgLevelReports, gsChatTreatment, canToggleLanguage } = useSelector(
    (state: any) => state.featureFlags?.variables || {},
  )

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const isMenuOpen = Boolean(anchorEl)
  const currentLanguage = i18n.language

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleModalOpen = () => setModalOpen(true)
  const handleModalClose = () => setModalOpen(false)

  const onClickToggleTheme = () => {
    toggleTheme()
    handleMenuClose()
  }

  const onClickToggleNav = () => {
    toggleNavStyle()
    handleMenuClose()
  }

  const onClickLogout = async () => {
    try {
      await Auth.signOut()
      clearLocalOrgInfo()
      window.location.reload()
    } catch (error) {
      console.error('Error signing out:', error)
    }
    handleMenuClose()
  }

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang)
    handleMenuClose()
  }

  const [yardsList, setYardsList] = useState([])

  useEffect(() => {
    if (yardDetailsList) {
      const newYardsList = transformYardList(organization, yardDetailsList)
      const orgModeEnabled = user?.attributes?.['custom:orgModeEnabled'] === 'true'

      if (orgModeEnabled && (appRouteLoadBoard || orgLevelReports)) {
        newYardsList.push({
          id: ORG_MODE_LIST_OBJECT.id,
          name: organization.charAt(0).toUpperCase() + organization.slice(1) + ORG_MODE_LIST_OBJECT.name,
        })
      }

      setYardsList(newYardsList)
    }
  }, [organization, yardDetailsList, user, appRouteLoadBoard, orgLevelReports])

  const handleYardChangeWrapper = (event, newValue: any) => {
    if (newValue) {
      handleYardChange({ target: { value: newValue.id } })
    }
  }

  const handleYardChange = event => {
    const selectedId = event.target.value

    if (selectedId === ORG_MODE_LIST_OBJECT.id && !inOrgMode) {
      dispatch(toggleOrgMode({ sub: user.attributes.sub }))

      if (appRouteLoadBoard) {
        if (window.location.href.includes('/app/settings/')) {
          navigate('/app/settings/dispatch/drivers')
        } else {
          navigate('/app/load-board/active-tickets')
        }
      } else if (orgLevelReports) {
        navigate('/app/reports/standard')
      }
    } else if (selectedId !== ORG_MODE_LIST_OBJECT.id && inOrgMode) {
      dispatch(toggleOrgMode({ sub: user.attributes.sub }))
    }

    if (selectedId !== ORG_MODE_LIST_OBJECT.id) {
      const selectedYardName = yardDetailsList?.find(yard => yard.id === selectedId)?.name
      window.localStorage.setItem(USER_SELECTED_YARD_LOCAL_STORAGE_KEY, selectedYardName)
      window.localStorage.setItem(USER_SELECTED_YARD_ID_LOCAL_STORAGE_KEY, selectedId)
    }

    window.location.reload()
  }

  const defaultYardValue = inOrgMode
    ? ORG_MODE_LIST_OBJECT.id
    : yardDetailsList?.find(yard => yard?.name === selectedYard)?.id || ''

  // Determine the default yard option from yardsList based on the computed id.
  const defaultYardOption: YardOption | null =
    yardsList.find((yard: YardOption) => yard.id === defaultYardValue) || null

  const handleClose = () => {
    window.localStorage.setItem('showSideMenuText', (!showSideMenuText).toString())
    dispatch(setShowSideMenuText(!showSideMenuText))

    if (onClose) {
      onClose()
    }
  }

  return (
    <Box sx={{ p: 1, py: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <img src={small_logo} alt="GreenSpark" style={{ width: 32, height: 32 }} />
          <Typography variant="subtitle1">GreenSpark</Typography>
        </Box>
        <IconButton size="small" onClick={handleClose}>
          {showSideMenuText ? <MenuOpenIcon /> : <MenuIcon />}
        </IconButton>
      </Box>

      <Autocomplete
        size="small"
        options={yardsList}
        disableClearable
        getOptionLabel={(option: YardOption) => option.name}
        value={defaultYardOption!}
        onChange={handleYardChangeWrapper}
        sx={{ mr: 2 }}
        renderInput={params => <TextField {...params} variant="outlined" sx={{ mx: 1 }} />}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-around', px: 1, py: 0 }}>
        {showSettings && (
          <IconButton
            size="medium"
            onClick={() => {
              if (inOrgMode) {
                navigate('/app/settings/dispatch/drivers')
              } else {
                navigate('/app/settings')
              }
            }}
            disabled={inOrgMode && !appRouteLoadBoard}
          >
            <SettingsIcon fontSize="medium" />
          </IconButton>
        )}
        <IconButton
          size="medium"
          onClick={() => window.open('https://greensparksoftware-academy.workramp.io/login', '_blank')}
        >
          <SchoolRounded fontSize="medium" />
        </IconButton>
        <IconButton
          size="medium"
          onClick={() => {
            dispatch(
              showFormModal({
                formModalName: FORM_MODALS.EQUIPMENT_SELECTOR,
              }),
            )
          }}
        >
          <SwitchCamera fontSize="medium" />
        </IconButton>
        <IconButton size="medium" onClick={() => window.open('https://intercom.help/greenspark-software/en', '_blank')}>
          <HelpIcon fontSize="medium" />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', px: 1, width: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
          <Chip
            icon={<PersonIcon />}
            label={`${user?.attributes?.given_name} ${user?.attributes?.family_name}`}
            variant="outlined"
            onClick={handleProfileMenuOpen}
            sx={{
              flexGrow: 1,
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
          />
          {gsChatTreatment && (
            <Button
              variant="outlined"
              onClick={handleModalOpen}
              startIcon={<Avatar alt="Sparky" src={small_logo} sx={{ width: 24, height: 24 }} />}
              sx={{
                borderColor: theme.palette.primary.main,
                borderWidth: 1,
                textTransform: 'none',
                height: 32,
              }}
            >
              Sparky
            </Button>
          )}
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
            },
          },
        }}
      >
        <MenuItem onClick={onClickToggleNav}>
          <ListItemIcon>
            {useCategoryNav ? <ViewModule fontSize="small" /> : <ViewList fontSize="small" />}
          </ListItemIcon>
          {`Switch to ${useCategoryNav ? 'Classic' : 'New'} Navigation`}
        </MenuItem>
        <MenuItem onClick={onClickToggleTheme}>
          <ListItemIcon>
            {currentTheme === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
          </ListItemIcon>
          {currentTheme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        </MenuItem>
        {canToggleLanguage && (
          <MenuItem onClick={() => changeLanguage(currentLanguage === 'en' ? 'es' : 'en')}>
            <ListItemIcon>
              <Language fontSize="small" />
            </ListItemIcon>
            {currentLanguage === 'en' || !currentLanguage ? t('language_spanish') : t('language_english')}
          </MenuItem>
        )}
        <MenuItem onClick={() => window.open('https://www.greensparksoftware.com/refer-someone', '_blank')}>
          <ListItemIcon>
            <AttachMoneyIcon fontSize="small" />
          </ListItemIcon>
          Refer a Friend!
        </MenuItem>
        <MenuItem onClick={onClickLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      <Modal
        open={modalOpen}
        onClose={handleModalClose}
        closeAfterTransition
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Fade in={modalOpen}>
          <Box
            sx={{
              width: '90%',
              maxWidth: '1200px',
              bgcolor: 'background.paper',
              borderRadius: '8px',
              boxShadow: 24,
              p: 2,
              outline: 'none',
            }}
          >
            <ChatComponentGemini />
          </Box>
        </Fade>
      </Modal>
    </Box>
  )
}

export default NavHeader
